import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import type { SeoMeta } from "@/types"
import { LOCATIONS, LOCATION_SLUGS } from "@/data/directory/registry"
import { rankOffices } from "@/data/rankings/scoring"
import { LOCATION_IMAGES, imageSrcSet } from "@/data/location-images"
import { cdnUrl } from "@/lib/cdn"

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

function IconTrophy({ className = "size-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0 1 16.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.023 6.023 0 0 1-2.77.852m0 0a17.867 17.867 0 0 1-1 3.172m1-3.172a6.022 6.022 0 0 1-2.77-.852" />
    </svg>
  )
}

function IconArrowRight({ className = "size-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          viewBox="0 0 20 20"
          fill={star <= Math.round(rating) ? "#C41E2A" : "#E8E4DF"}
          className="size-3.5"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 0 0 .95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 0 0-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 0 0-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 0 0-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 0 0 .951-.69l1.07-3.292Z" />
        </svg>
      ))}
      <span className="ml-1 text-xs font-medium text-slate">{rating.toFixed(1)}</span>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Location card                                                       */
/* ------------------------------------------------------------------ */

function RankingsLocationCard({ slug }: { slug: string }) {
  const location = LOCATIONS[slug]
  if (!location) return null

  const topAgent = rankOffices(location.offices)[0]

  return (
    <Link
      href={`/best/${slug}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-stone bg-white transition-all hover:border-stone/80 hover:shadow-md"
    >
      {LOCATION_IMAGES[slug] && (
        <div className="relative h-44 overflow-hidden">
          <OptimizedImage
            src={cdnUrl(LOCATION_IMAGES[slug].url)}
            srcSet={imageSrcSet(LOCATION_IMAGES[slug].url)}
            sizes="(max-width: 640px) 100vw, 50vw"
            alt={LOCATION_IMAGES[slug].alt}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            width={800}
            height={340}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        </div>
      )}
      <div className="flex-1 p-8">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-red/10">
            <IconTrophy className="size-5 text-red" />
          </div>
          <h2 className="font-display text-2xl font-bold text-charcoal">
            {location.name}
          </h2>
        </div>

        <p className="mt-4 text-sm text-slate">
          {location.offices.length} agents ranked by reviews, experience, and
          local market expertise.
        </p>

        {topAgent && (
          <div className="mt-5 rounded-lg border border-stone bg-cream/50 px-4 py-3">
            <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-warm-grey">
              Top ranked
            </p>
            <p className="mt-0.5 text-sm font-semibold text-charcoal">
              {topAgent.name} — {topAgent.branch}
            </p>
            <StarRating rating={topAgent.rating} />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-stone px-8 py-4">
        <span className="text-sm font-semibold text-red transition-colors group-hover:text-deep-red">
          See {location.name} rankings
        </span>
        <IconArrowRight className="size-4 text-red transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function BestIndex({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Rankings
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Best estate agents 2026<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            The top-ranked estate agents across the UK, scored by reviews,
            experience, listings volume, and local market expertise.
          </p>
        </div>
      </section>

      {/* Location cards */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <div className="grid gap-6 sm:grid-cols-2">
            {LOCATION_SLUGS.map((slug) => (
              <RankingsLocationCard key={slug} slug={slug} />
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How we rank agents<span className="text-red">.</span>
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-slate">
            <p>
              Every office is scored using a weighted composite of four factors:
              customer rating (40%), review volume (25%), years of experience
              (20%), and active listing count (15%). This balances quality,
              reputation, track record, and current market presence.
            </p>
            <p>
              Rankings are updated regularly and based on publicly available
              data. Browse detailed profiles in our{" "}
              <Link href="/directory" className="text-red hover:text-deep-red">
                estate agents directory
              </Link>{" "}
              or read our{" "}
              <Link href="/areas" className="text-red hover:text-deep-red">
                neighbourhood area guides
              </Link>{" "}
              for local property insights. If you believe your office details
              are incorrect,{" "}
              <Link href="/contact" className="text-red hover:text-deep-red">
                get in touch
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Are you a top-rated agent<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            Stay ahead of the competition with video marketing that wins
            instructions. Turn your property listings into scroll-stopping video
            ads in under a minute.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Request a demo
            </Link>
            <Link
              href="/how-it-works"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-6 py-3 text-base font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              See how it works
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
